import { differenceInMonths, format } from 'date-fns';
import type { BarSeriesOption, LineSeriesOption } from 'echarts/charts';
import type {
  DatasetComponentOption,
  GridComponentOption,
  TitleComponentOption,
  TooltipComponentOption,
} from 'echarts/components';
import type { ComposeOption } from 'echarts/core';
import type { FC } from 'react';
import ReactDOMServer from 'react-dom/server';
import { useChartContext } from '../../Chart/context';
import { ClientECharts } from '../../Chart/components';
import { Bar, AmountForDateTooltip, displayMonetaryAmount } from '../..';

type ECOption = ComposeOption<
  | BarSeriesOption
  | LineSeriesOption
  | TitleComponentOption
  | TooltipComponentOption
  | GridComponentOption
  | DatasetComponentOption
>;

export interface ChartProps extends Pick<BarSeriesOption, 'barWidth' | 'barMinHeight'> {
  data: Bar[];
  className?: string;
}

export const Chart: FC<ChartProps> = ({ data, barWidth, barMinHeight, className }) => {
  const { rangeFilter } = useChartContext();
  const maxYValue = Math.max(...data.map((d) => d.value));
  const minYValue = Math.min(...data.map((d) => d.value));
  const hasNegativeValues = minYValue < 0;
  const hasPositiveValues = maxYValue > 0;
  const allZeros = !hasNegativeValues && !hasPositiveValues;

  const intervalCalculation = () => {
    const absDifference =
      (hasPositiveValues ? Math.abs(maxYValue) / 4 : 0) + (hasNegativeValues ? Math.abs(minYValue) / 4 : 0);
    const intervalRoundingFigures = Math.pow(10, String(absDifference).replace('.', '').replace('-', '').length - 2);
    const interval =
      Math.round(
        ((hasPositiveValues ? Math.abs(maxYValue) / 4 : 0) + (hasNegativeValues ? Math.abs(minYValue) / 4 : 0)) /
          intervalRoundingFigures
      ) * intervalRoundingFigures;

    return interval;
  };
  const option: ECOption = {
    tooltip: {
      padding: [0, 0],
      renderMode: 'auto',
      className: 'w-auto h-auto',
      shadowColor: 'transparent',
      borderColor: 'transparent',
      backgroundColor: 'transparent',
      formatter(params) {
        const paramsData = Array.isArray(params) ? params[0] : params;
        const data = paramsData.data as Bar;
        return ReactDOMServer.renderToString(
          <AmountForDateTooltip
            date={data.dateISO}
            value={displayMonetaryAmount(data.monetaryAmount, {
              withoutDollarSign: true,
            })}
          />
        );
      },
      position: (point) => {
        return [point[0], point[1]];
      },
      trigger: 'axis',
      axisPointer: {
        type: 'line',
        lineStyle: { color: '#FFFFFF80' },
      },
    },
    grid: { bottom: 43.45, left: 0, right: allZeros ? 0 : 65, top: 0 },
    xAxis: [
      {
        axisLabel: {
          fontSize: 16,
          interval: 6,
          margin: 26.45,
          lineHeight: 17,
          color: '#585F66',
          align: 'left',
          showMinLabel: true,
          formatter(index) {
            const dataPoint = data[Number(index)];
            const rangeMonthDiff = differenceInMonths(rangeFilter[1], rangeFilter[0]);
            const formatting = rangeMonthDiff >= 4 ? 'MMM yyy' : 'MMM dd';
            return format(new Date(dataPoint.dateISO), formatting);
          },
        },
        type: 'category',
        axisLine: { show: false },
        axisTick: { show: false },
      },
    ],
    yAxis: [
      {
        axisLabel: {
          margin: 9,
          fontSize: 12,
          lineHeight: 17,
          fontWeight: 500,
          verticalAlign: 'top',
          color: '#585F66',
          showMinLabel: false,
          showMaxLabel: false,
          formatter(value: number) {
            return displayMonetaryAmount(
              { currencyCode: 'USD', value: value },
              { notation: 'compact', maximumFractionDigits: 2 }
            );
          },
        },
        splitLine: { show: false },
        type: 'value',
        minInterval: 4,
        interval: intervalCalculation(),
        position: 'right',
      },
    ],

    series: [
      {
        type: 'bar',
        barGap: '1.75px',
        barMinHeight: barMinHeight ?? 15,
        itemStyle: {
          borderRadius: 100,
        },
        barWidth: barWidth ?? '14.57px',
        data,
      },
    ],
  };
  return <ClientECharts className={className} option={option} />;
};
